import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Rises into place the first time it scrolls into view, like the hero's entrance and the professionals' copy. Rendered
 * visible on the server and left alone when it is already on screen at hydration (no flash); only content still below
 * the fold is hidden, and it appears once 10% past the bottom edge. Under reduced motion it is simply there.
 */
export function Reveal(props: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<'static' | 'waiting' | 'shown'>('static')

  useEffect(() => {
    const node = ref.current
    if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (node.getBoundingClientRect().top < window.innerHeight) return
    // oxlint-disable-next-line react/set-state-in-effect -- hydration parity: the server can't know what is below the fold, so hiding waits for the client
    setState('waiting')
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        setState('shown')
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        state !== 'static' && 'transition-[opacity,translate] duration-700 ease-out',
        state === 'waiting' && 'translate-y-5 opacity-0',
        props.className,
      )}
      style={state === 'shown' ? { transitionDelay: `${props.delay ?? 0}ms` } : undefined}
    >
      {props.children}
    </div>
  )
}
